import type { AdaptationSuggestion } from '../dto/AdaptationSuggestion';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { AppPreferencesRepository } from '../ports/AppPreferencesRepository';
import type { Clock } from '../ports/Clock';
import type { DailyRoutineRepository } from '../ports/DailyRoutineRepository';
import type { DailyStepRepository } from '../ports/DailyStepRepository';
import { runAdaptationCycle } from '../../domain/adaptive-guidance/AdaptationCycle';
import {
  DEFAULT_ADAPTIVE_GUIDANCE_PREFERENCES,
  fromAdaptiveGuidancePreferencesData,
} from '../../domain/preferences/AdaptiveGuidancePreferences';
import type { ProfileId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';

export class GetAdaptationSuggestion {
  public constructor(
    private readonly dailyRoutines: DailyRoutineRepository,
    private readonly dailySteps: DailyStepRepository,
    private readonly appPreferences: AppPreferencesRepository,
    private readonly clock: Clock,
  ) {}

  public async execute(
    profileId: ProfileId,
    logicalDate: string,
  ): Promise<Result<AdaptationSuggestion, RoutineError>> {
    try {
      const routine = await this.dailyRoutines.findByLogicalDate(profileId, logicalDate);
      if (!routine) return failure(new RoutineError('routine_not_found', 'Journée introuvable.'));
      const steps = await this.dailySteps.listByRoutine(routine.id);
      const preferencesEntity = await this.appPreferences.findByProfileId(profileId);
      const preferences =
        (preferencesEntity && fromAdaptiveGuidancePreferencesData(preferencesEntity.data)) ??
        DEFAULT_ADAPTIVE_GUIDANCE_PREFERENCES;
      const priorChoice = preferences.decisions[logicalDate];

      const cycle = runAdaptationCycle({
        dayMode: routine.dayMode,
        steps,
        timestamp: this.clock.now().toISOString(),
        agsEnabled: preferences.agsEnabled,
        ...(priorChoice ? { priorChoice } : {}),
      });

      return success({
        decisionType: cycle.decision.type,
        guidanceCode: cycle.explanation.code,
        modeKey: cycle.explanation.modeKey,
        affectedCount: cycle.explanation.affectedCount,
        ...(cycle.decision.option
          ? {
              ruleId: cycle.decision.option.ruleId,
              proposedOrder: cycle.decision.option.proposedOrder,
              affectedStepIds: cycle.decision.option.affectedStepIds,
            }
          : {}),
      });
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }
}
